import { gql } from '@apollo/client';

/**
 * System configuration — the reads behind the console's Configuration tabs.
 *
 * These are app_admin's own documents, which is deliberate: the screens differ
 * but the contract must not, or the two consoles would disagree about what a
 * setting is while both were still in use.
 */

/* ── risk configuration ─────────────────────────────────────────────────────
   The settings and scenarios every risk figure in the app is computed against:
   a settings row names the model, a stress scenario is a shock applied to it,
   a sensitivity scenario is a factor moved on its own, and a simulation
   configuration is what a parametric run is given. The "multi" variants are
   scenarios that move several things at once. */

export const GET_ALL_RISK_SETTINGS = gql`
  query GetAllRiskSettingsForUser {
    getAllRiskSettingsForUser {
      createdAt
      description
      endDate
      id
      isActive
      isGlobal
      name
      riskSensitivityScenarioIds
      riskStressScenarioIds
      startDate
      userId
    }
  }
`;

export const GET_ALL_RISK_STRESS_SCENARIOS = gql`
  query GetAllRiskStressScenarios {
    allRiskStressScenarios {
      description
      historicalEndDate
      historicalStartDate
      id
      isActive
      isHistorical
      isStandardDeviation
      numberOfStandardDeviations
      scenarioName
      scenarioValue
      securitySymbol
      securityType
      shockValueNegativeAmount
      shockValueNegativePercent
      shockValuePositiveAmount
      shockValuePositivePercent
    }
  }
`;

export const GET_ALL_RISK_SENSITIVITY_SCENARIOS = gql`
  query GetAllRiskSensitivityScenarios {
    allRiskSensitivityScenarios {
      id
      isStandardDeviation
      numberOfStandardDeviations
      scenarioName
      securitySymbol
      securityType
      shockValueNegativeAmount
      shockValueNegativePercent
      shockValuePositiveAmount
      shockValuePositivePercent
      transitivityType
    }
  }
`;

export const GET_ALL_MULTI_RISK_STRESS_SCENARIOS = gql`
  query GetAllMultiRiskStressScenarios {
    allMultiRiskStressScenarios {
      id
      scenarioName
      description
      simulataneousRiskStressScenarioIds
    }
  }
`;

export const GET_ALL_MULTI_RISK_SENSITIVITY_SCENARIOS = gql`
  query GetAllMultiRiskSensitivityScenarios {
    allMultiRiskSensitivityScenarios {
      id
      scenarioName
      description
      simulataneousRiskSensitivityScenarioIds
    }
  }
`;

export const GET_ALL_RISK_SIMULATION_CONFIGURATIONS = gql`
  query GetAllRiskSimulationConfigurations($userTag: String!) {
    riskSimulationConfigurationsByUser(userTag: $userTag) {
      id
      userTag
      ownerId
      scenarioName
      scenarioValue
      halfLifeDays
      historicWindowSize
      isExponential
      currency
      numberOfSimulations
      forecastingHorizon
      isGlobal
      description
      createdAt
    }
  }
`;

/* ── attribution ───────────────────────────────────────────────────────────
   Which model and windows an attribution run uses. */

export const GET_ALL_ATTRIBUTION_SETTINGS = gql`
  query GetAllAttributionSettings($skip: Int, $limit: Int) {
    getAllAttributionSettings(skip: $skip, limit: $limit) {
      success
      message
      settings {
        id
        name
        description
        attributionModel
        securityGroupIds
        timePeriod
        frequency
        isOndemand
        isActive
      }
    }
  }
`;

/* ── forecasts ─────────────────────────────────────────────────────────────
   The expected returns goal planning is built on. A scenario is a named set of
   assumptions; one of them is active, and that is the one the planner reads. */

export const GET_ACTIVE_FORECAST_SCENARIO = gql`
  query GetActiveForecastScenario {
    getActiveForecastScenario {
      id
      name
      asOfDate
      source
      isActive
      isBaseline
    }
  }
`;

export const GET_FORECAST_SCENARIOS = gql`
  query GetForecastScenarios($skip: Int, $limit: Int) {
    getForecastScenarios(skip: $skip, limit: $limit) {
      id
      name
      description
      asOfDate
      source
      methodologyNotes
      isActive
      isBaseline
    }
  }
`;

export const GET_FORECAST_ASSUMPTIONS = gql`
  query GetForecastAssumptions($scenarioId: Int!) {
    getForecastAssumptions(scenarioId: $scenarioId) {
      id
      scenarioId
      assetClassId
      modelId
      timeHorizonYears
      earningsGrowthRate
      earningsGrowthPeriod
      currentPeRatio
      historicalPeRatio
      targetPeRatio
      dividendYield
      valuationImpact
      currentYield
      yieldToMaturity
      durationYears
      creditSpread
      defaultRate
      recoveryRate
      historicalVolatility
      expectedVolatility
      inflationAssumption
      realReturnTarget
      expectedAlpha
      expectedFees
      confidenceLevel
      benchmarkName
      benchmarkDescription
      methodology
      methodologyCode
      computationParameters
      dataSources
      notes
      computedReturns {
        timeHorizonYears
        expectedReturn
        standardDeviation
        expectedAlpha
        expectedFees
        netFeeAlpha
        totalExpectedReturn
      }
    }
  }
`;

export const GET_ASSET_CLASSES = gql`
  query GetAssetClasses($language: String) {
    getAssetClasses(language: $language) {
      id
      name
      classType
      description
      parentId
      parent {
        id
        name
        classType
        description
      }
      children {
        id
        name
        classType
        description
      }
    }
  }
`;


/* ── universes ─────────────────────────────────────────────────────────────
   The benchmark and security-group documents are NOT here: the analytics layer
   already has them, and a second document under the same operation name would
   make every refetch of one fire the other.

   Supported securities and the field-metadata LIST are also absent, and that
   is not an omission — see the note on those tabs. Their resolvers are
   commented out of the backend's unified schema, so there is nothing to
   query. */

/* ── tags ──────────────────────────────────────────────────────────────────
   The vocabulary applied to clients, funds and reports. */

export const GET_USER_DEFINED_TAGS = gql`
  query GetUserDefinedTags {
    userDefinedTagsByUser {
      createdAt
      description
      displayName
      hasPredefinedValues
      id
      internalReferenceName
      isBasic
      isFree
      isOnDemand
      isPremium
      predefinedValues
      tagType
      userId
    }
  }
`;

/* ── labels and translations ───────────────────────────────────────────────
   Every user-facing label, per language, with the prompt the agents are given
   for it. Read at runtime, so a word changed here changes the product. */

export const GET_UI_LABELS = gql`
  query GetUiLabels($labelGroup: String, $languageCode: String) {
    getUiLabels(labelGroup: $labelGroup, languageCode: $languageCode) {
      success
      message
      labels {
        value
        displayValue
        labelGroup
        description
        agentPrompt
      }
    }
  }
`;

/* The tables whose translations can be read and edited. `key` ("mf-holdings")
   is what is passed back as `table` below — the API refuses the display name.
   People see the app's own name for it (lib/translationTables.js), falling
   back to `displayName`, never the key as it arrives. */
export const GET_TRANSLATION_TABLES = gql`
  query GetTranslationTables {
    getTranslationTables {
      success
      message
      tables {
        key
        displayName
      }
    }
  }
`;

/* One translated record: the original (English) record's id, the language,
   whether a person has verified it, and each translatable field's original
   beside its translation (null when not translated yet). Shared by the read
   and the save. */
export const TABLE_TRANSLATION_ITEM_FIELDS = `
  id
  baseId
  languageCode
  isHumanVerified
  fields {
    fieldName
    originalValue
    translatedValue
  }
`;

/* One page of a table's translations: `limit` records, then the next page by
   passing `nextCursor` back as `after`. Cursors are opaque. */
export const GET_TABLE_TRANSLATIONS = gql`
  query GetTableTranslations(
    $table: String!
    $languageCode: String
    $limit: Int
    $after: String
    $fields: [String!]
  ) {
    getTableTranslations(
      table: $table
      languageCode: $languageCode
      limit: $limit
      after: $after
      fields: $fields
    ) {
      success
      message
      table
      hasMore
      nextCursor
      items {
        ${TABLE_TRANSLATION_ITEM_FIELDS}
      }
    }
  }
`;

/* How a translation correction sweep is going: one table's, by its key, or the
   cache sweep's with no table. Staff only — anyone else gets success: false. */
export const GET_TRANSLATION_CORRECTION_PROGRESS = gql`
  query GetTranslationCorrectionProgress($table: String) {
    getTranslationCorrectionProgress(table: $table) {
      success
      message
      table
      status
      counters {
        chunks
        scanned
        sent
        reused
        changed
        written
      }
      languagesDone
      enqueuedAt
      startedAt
      updatedAt
      finishedAt
      lastError
    }
  }
`;

/* The background runs that have been started — a correction sweep among them —
   optionally narrowed to one subject (a table key). */
export const GET_TRIGGERED_EVENTS = gql`
  query GetTriggeredEvents($subject: String) {
    getTriggeredEvents(subject: $subject) {
      kind
      key
      eventType
      state
      subject
      source
      enqueuedAt
      cancelled
    }
  }
`;

/* ── NSE to broker-dealer mapping ──────────────────────────────────────────
   A scheme is only placeable once the exchange's code is mapped to the
   broker-dealer's own: an unmapped scheme looks fine everywhere else and fails
   at the order.

   `isMapped` is a FILTER here and not a field — `MFSchemeInfo` has no such
   column, whatever app_admin's own document selects. So the screen asks the
   server for the mapped or the unmapped set rather than labelling rows.

   The lean selection is app_admin's: the full scheme row is far too heavy to
   page through a whole filtered universe. */

export const GET_MF_SCHEMES_MAPPING = gql`
  query MfSchemesMapping(
    $limit: Int! = 50
    $offset: Int! = 0
    $searchQuery: String
    $amcCode: String
    $schemeCategory: String
    $isMapped: Boolean
  ) {
    mfSchemes(
      limit: $limit
      offset: $offset
      searchQuery: $searchQuery
      amcCode: $amcCode
      schemeCategory: $schemeCategory
      isMapped: $isMapped
    ) {
      total
      totalIsins
      schemes {
        schemeCode
        schemeName
        isin
        rtaSchemeCode
        amcSchemeCode
      }
    }
  }
`;

/* ── investor Q&A knowledge base ───────────────────────────────────────────
   The question/answer pairs behind the embedding index the assistant answers
   investors from. Editing what is in here changes what it says.

   BOTH FILTERS ARE THE SERVER'S, not a client-side narrowing of a page:
   `archetype` matches the tag, and `question` runs a SEMANTIC search over the
   embeddings — so a different query returns a different candidate set, not the
   same rows reordered. Filtering locally would quietly turn a semantic search
   into a substring match.

   `skip` and `limit` are required. app_admin declares them nullable and gets
   away with it only by always passing values.

   Read through `getInvestorQaEntries` rather than `getInvestorQaEmbeddings`:
   same arguments, but it does not send the embedding vectors, which this
   screen never shows. Its answer and tag are named `aiAnswer` and
   `investorArchetype`; they are aliased back to `answer` and `archetype`, the
   names the screen reads. */

export const GET_INVESTOR_QA_ENTRIES = gql`
  query GetInvestorQaEntries($skip: Int!, $limit: Int!, $archetype: String, $question: String) {
    getInvestorQaEntries(skip: $skip, limit: $limit, archetype: $archetype, question: $question) {
      total
      entries {
        id
        question
        answer: aiAnswer
        archetype: investorArchetype
        createdAt
        updatedAt
      }
    }
  }
`;

/* The VR bulk exports a table load can read from S3: the run ids (newest
   first) and the table names, each a plain list of strings. They fill the
   pickers on the "VR table from S3" data update. */
export const VR_BULK_RUN_IDS = gql`
  query VrBulkRunIds {
    vrBulkRunIds
  }
`;

export const VR_BULK_TABLES = gql`
  query VrBulkTables {
    vrBulkTables
  }
`;
